import { DataApi, OttoAdapter } from "@proofgeist/fmdapi";
import { BaseFetchAdapter } from "@proofgeist/fmdapi/adapters/fetch-base.js";

const FILEMAKER_CONFIG = {
  apiUrl: process.env.REACT_APP_FILEMAKER_API_URL,
  apiToken: process.env.REACT_APP_FILEMAKER_API_TOKEN,
  database: process.env.REACT_APP_FILEMAKER_DATABASE || "yooladmin",
  timeout: 10000,
};

let client;

class BearerTokenAdapter extends BaseFetchAdapter {
  constructor(options) {
    super({ server: options.server, db: options.db, refreshToken: false });
    this.token = options.token;
  }

  getToken = async () => {
    return this.token;
  };
}

function getClient() {
  if (client) return client;
  if (!FILEMAKER_CONFIG.apiUrl || !FILEMAKER_CONFIG.apiToken || !FILEMAKER_CONFIG.database) {
    throw new Error("Configuración de FileMaker incompleta");
  }

  const token = String(FILEMAKER_CONFIG.apiToken || "");
  const isOttoKey = token.startsWith("KEY_") || token.startsWith("dk_");

  client = DataApi({
    adapter: isOttoKey
      ? new OttoAdapter({
          auth: { apiKey: token },
          db: FILEMAKER_CONFIG.database,
          server: FILEMAKER_CONFIG.apiUrl,
        })
      : new BearerTokenAdapter({
          token,
          db: FILEMAKER_CONFIG.database,
          server: FILEMAKER_CONFIG.apiUrl,
        }),
  });
  return client;
}

class ProofkitFilemakerService {
  async getAllRecords(layout, options = {}) {
    try {
      const c = getClient();

      const limitRaw = options?.limit ?? options?._limit;
      const offsetRaw = options?.offset ?? options?._offset;

      const limit = Number.isFinite(Number(limitRaw)) ? Number(limitRaw) : 100;
      const offset = Number.isFinite(Number(offsetRaw)) ? Number(offsetRaw) : 1;

      const rows = [];
      let foundCount = 0;
      let nextOffset = offset;

      while (true) {
        const resp = await c.request({
          url: `/layouts/${layout}/records`,
          query: {
            _limit: limit,
            _offset: nextOffset,
          },
          timeout: FILEMAKER_CONFIG.timeout,
        });

        const page = Array.isArray(resp?.data) ? resp.data : [];
        rows.push(...page);

        if (typeof resp?.dataInfo?.foundCount === "number") {
          foundCount = resp.dataInfo.foundCount;
        }

        if (page.length === 0) break;
        if (page.length < limit) break;
        if (foundCount > 0 && rows.length >= foundCount) break;

        nextOffset += limit;
      }

      return {
        success: true,
        data: rows,
        foundCount: foundCount || rows.length,
        returnedCount: rows.length,
      };
    } catch (error) {
      console.error(`Error listing all records in layout ${layout}:`, error);
      throw new Error(`Error al obtener registros: ${error?.message || String(error)}`);
    }
  }

  async findRecords(layout, options = {}) {
    try {
      const c = getClient();
      const { query, sort, limit = 100, offset = 1 } = options;
      const res = await c.find({
        layout,
        query: query || [],
        sort: sort || [],
        limit,
        offset,
        timeout: FILEMAKER_CONFIG.timeout,
      });

      return {
        success: true,
        data: Array.isArray(res?.data) ? res.data : [],
        foundCount: res?.dataInfo?.foundCount || 0,
        returnedCount: res?.dataInfo?.returnedCount || 0,
      };
    } catch (error) {
      console.error(`Error finding records in layout ${layout}:`, error);
      throw new Error(`Error al buscar registros: ${error?.message || String(error)}`);
    }
  }

  async createRecord(layout, fieldData, options = {}) {
    try {
      const c = getClient();
      const res = await c.create({
        layout,
        fieldData: fieldData || {},
        timeout: FILEMAKER_CONFIG.timeout,
        ...options,
      });
      return { success: true, recordId: res?.recordId, modId: res?.modId };
    } catch (error) {
      console.error(`Error creating record in layout ${layout}:`, error);
      throw new Error(`Error al crear registro: ${error?.message || String(error)}`);
    }
  }

  async getRecord(layout, recordId, options = {}) {
    try {
      const c = getClient();
      const res = await c.get({
        layout,
        recordId,
        timeout: FILEMAKER_CONFIG.timeout,
        ...options,
      });
      return { success: true, data: res?.data?.[0] ? res.data[0] : null };
    } catch (error) {
      console.error(`Error getting record ${recordId} in layout ${layout}:`, error);
      throw new Error(`Error al obtener registro: ${error?.message || String(error)}`);
    }
  }

  async updateRecord(layout, recordId, fieldData, options = {}) {
    try {
      const c = getClient();
      const res = await c.update({
        layout,
        recordId,
        fieldData: fieldData || {},
        timeout: FILEMAKER_CONFIG.timeout,
        ...options,
      });
      return { success: true, modId: res?.modId };
    } catch (error) {
      console.error(`Error updating record ${recordId} in layout ${layout}:`, error);
      throw new Error(`Error al actualizar registro: ${error?.message || String(error)}`);
    }
  }

  async deleteRecord(layout, recordId, options = {}) {
    try {
      const c = getClient();
      const res = await c.delete({
        layout,
        recordId,
        timeout: FILEMAKER_CONFIG.timeout,
        ...options,
      });
      return { success: true, response: res };
    } catch (error) {
      console.error(`Error deleting record ${recordId} in layout ${layout}:`, error);
      throw new Error(`Error al eliminar registro: ${error?.message || String(error)}`);
    }
  }

  async runScript(layout, recordId, script, scriptParam = "") {
    try {
      const c = getClient();
      const scriptName = String(script || "").trim();
      if (!scriptName) {
        throw new Error("Nombre de script inválido");
      }
      const rid = String(recordId ?? "").trim();
      if (!rid) {
        throw new Error("RecordId inválido");
      }

      const resp = await c.request({
        url: `/layouts/${layout}/records/${rid}`,
        query: {
          script: scriptName,
          "script.param": String(scriptParam ?? ""),
        },
        timeout: FILEMAKER_CONFIG.timeout,
      });

      const scriptError = resp?.scriptError;
      const scriptResult = resp?.scriptResult;
      if (scriptError === undefined && scriptResult === undefined) {
        const fallback = await c.executeScript({
          layout,
          script: scriptName,
          scriptParam: String(scriptParam ?? ""),
          timeout: FILEMAKER_CONFIG.timeout,
        });
        return {
          success: true,
          scriptResult: fallback?.scriptResult,
          scriptError: fallback?.scriptError,
          data: null,
        };
      }

      return {
        success: true,
        scriptResult,
        scriptError,
        data: Array.isArray(resp?.data) ? resp.data?.[0] : null,
      };
    } catch (error) {
      console.error(`Error running script '${script}' in layout ${layout}:`, error);
      throw new Error(`Error al ejecutar script: ${error?.message || String(error)}`);
    }
  }
}

const proofkitFilemakerService = new ProofkitFilemakerService();

export { proofkitFilemakerService };
export default proofkitFilemakerService;
